import React, { memo, useState, useContext } from 'react'
import { ICarrier } from 'types/contractLanes'
import { callApi } from 'utils/api/callApi'
import { RoutingGuideRowProps } from 'apps/marketplace/apiTypes/carrierRoutingGuides'
import { IRateAPI } from 'apps/marketplace/store/contractLanes/types'
import { RADIUS_RATE } from 'apps/marketplace/utils/constants/rates'
import { prepareSaveLanesRequestData } from 'apps/marketplace/utils/helpers/lanes'
import { ReducerActions } from 'apps/marketplace/utils/reducers/contractLaneModal'
import { useToastState } from 'transfix-ui'
import { EditLaneModal } from './EditLaneModal'
import { EditLaneModalContext } from './contexts'

interface IRoutingGuideModalProps {
  carrier?: Partial<ICarrier>
  creationSource: string
  specificLaneId?: string
  transfixLaneId?: string
  dataOnSuccess?: (data: RoutingGuideRowProps) => void
  refreshOnSuccess?: () => void
}

export const RoutingGuideEditComponent = memo<IRoutingGuideModalProps>(
  ({
    carrier,
    creationSource,
    dataOnSuccess,
    refreshOnSuccess,
    specificLaneId,
    transfixLaneId,
  }) => {
    const { dispatch: setEditLaneModalState, state: editLaneModalState } = useContext(
      EditLaneModalContext
    )
    const confirmAddLaneInitialState = {
      isCarrierActive: false,
      carrierId: undefined,
    }
    const [, setConfirmAddLaneState] = useState(confirmAddLaneInitialState)
    const {
      carrier: specificCarrier,
      currentContract: editLaneCurrentContract,
      error: editLaneError,
      laneAlert,
      showModal: showEditLaneModal,
      specificLane,
    } = editLaneModalState
    const { deliveryMiles, id: laneAlertId, pickupMiles } = laneAlert || {}
    const hideModal = { type: ReducerActions.hide, currentContract: undefined }
    const closeEditLaneModalHandler = () => setEditLaneModalState(hideModal)
    const { openToast } = useToastState()

    const addEditRecurringRateRequest = ({
      addRate,
      data,
      laneId,
    }: {
      data: IRateAPI
      addRate: boolean
      laneId?: string
    }) => {
      setEditLaneModalState({ type: ReducerActions.request })
      const requestData = prepareSaveLanesRequestData(data, creationSource, laneId, laneAlertId)
      callApi(requestData)
        .then(response => {
          setEditLaneModalState({ type: ReducerActions.success })
          if (addRate) {
            const isRadiusRate = data.rateType === RADIUS_RATE
            const returnedCarrier = isRadiusRate
              ? response.carrierContractLanes[0].carrier
              : response
            if (transfixLaneId && specificLaneId) {
              openToast({ toastType: 'default', toastMessage: 'Lane Rate Added Successfully!' })
            } else {
              setConfirmAddLaneState({
                carrierId: returnedCarrier.id,
                isCarrierActive: returnedCarrier.state === 'activated',
              })
            }
          }

          if (refreshOnSuccess) {
            refreshOnSuccess()
          }
          if (dataOnSuccess && response) {
            dataOnSuccess(response)
          }
        })
        .catch(errors => {
          setEditLaneModalState({ type: ReducerActions.error, errors })
        })
    }

    const addRateHandler = (data: IRateAPI, laneId?: string) =>
      addEditRecurringRateRequest({ data, addRate: true, laneId })
    const editRateHandler = (data: IRateAPI, laneId?: string) =>
      addEditRecurringRateRequest({ data, addRate: false, laneId })

    return (
      <>
        {showEditLaneModal && (
          <EditLaneModal
            dialogOpen={showEditLaneModal}
            dialogData={editLaneCurrentContract}
            errors={editLaneError}
            closeLaneSidebarDialog={closeEditLaneModalHandler}
            addRecurringRate={addRateHandler}
            editRecurringRate={editRateHandler}
            specificLaneId={specificLaneId}
            specificLane={specificLane}
            carrier={carrier || specificCarrier}
            creationSource={creationSource}
            pickupRadiusMiles={pickupMiles}
            deliveryRadiusMiles={deliveryMiles}
          />
        )}
      </>
    )
  }
)
